'use strict'

import UserApi from '@/api/user.js'
import Security from '@/_helpers/security.js'
import AccessTokenApi from '@/api/access_token.js'
import axios from 'axios';

import { ADMIN_ROLES, ROLE_COMPANY_ADMIN, ROLE_COMPANY_USER } from '@/_helpers/constants.js'

import { setFullName } from '@/_helpers/utils.js'
import notifications from './user/notifications.js'

const getInitialModalAlertParams = () => {
  return {
    show: false,
    options: null
  }
}

const state = {
  loading: false,
  error: null,
  route: null,
  data: null,
  meta: null,
  change_user: false,

  modals: {
    alert: getInitialModalAlertParams()
  },

  result: {
    loginSuccess: null,
    fetchTokenSuccess: null,
    passwordUpdateSuccess: null,
    passwordRecoveryRequestSuccess: null
  }
}

const actions = {
  fetchToken ({commit}, {token}) {
    commit('fetchToken')

    return AccessTokenApi.fetchOnePasswordRecovery(token)
      .then((accessToken) => {
        Security.applyAccessToken(accessToken)

        return accessToken
      })
      .then((accessToken) => commit('fetchTokenSuccess', accessToken))
      .catch(err => {
        commit('fetchTokenFailure', err);
        return Promise.reject(err)
      })
  },

  login ({commit, dispatch}, {email, password, captcha_token, redirect = '/'}) {
    commit('login')

    return Security.login({email}, password, captcha_token)
      .then((user) => {
        commit('loginSuccess')
        return user
      })
      .catch(err => {
        return Promise.reject(err);
      })
  },

  passwordRecovery ({commit, dispatch}, {email}) {
    commit('passwordRecovery')

    return AccessTokenApi.storeOnePasswordRecovery({email})
      .then(data => {
        commit('passwordRecoverySuccess', data)
      })
      .catch(err => {
        commit('passwordRecoveryFailure', err);
        return Promise.reject(err)
      })
  },

  save ({commit}, data) {
    commit('save')

    return UserApi.updateOne(data)
      .then(data => commit('saveSuccess', data))
      .catch(err => {
        commit('saveFailure', err);
        throw err;
      })
  },

  createUser ({commit}, model) {
    return UserApi
      .storeOne({...model, roles: [{id: '1'}]})
  },

  userInfoRequest ({commit}, data) {
    commit('userInfoRequest')

    return UserApi.fetchOneUser(data)
      .then(data => commit('userInfoRequestSuccess', data))
      .catch(err => commit('userInfoRequestFailure', err))
  },

  saveUserLogoRequest ({commit}, data) {
    const email = data.email
    const logo = data.logo

    commit('saveUserLogoRequest')

    UserApi.saveUserLogo(email, logo)
      .then(data => commit('saveUserLogoSuccess', data))
      .catch(err => commit('saveUserLogoFailure', err))
  },

  userEmailConfirm ({commit}, {hash, locale}) {
    UserApi.requestUserEmailConfirm({hash, locale})
      .then(data => commit('saveNewEmail', data))
      .catch(err => commit('saveFailure', err))
  },

  updateUserPassword ({commit, dispatch}, newPassword) {
    commit('updateUserPasswordInit')

    AccessTokenApi.updateUserPassword(newPassword)
    .then(data => {
      dispatch('login', {email: data.email, password: newPassword})
    })
    .catch(err => commit('updateUserPasswordFailure', err))
  },

  clearModalAlertData ({ commit }) {
    commit('clearModalAlertData')
  }
}

const mutations = {
  fetchToken (state) {
    state.error = null
    state.loading = true
    state.result.fetchTokenSuccess = null
  },

  fetchTokenSuccess (state) {
    state.loading = false
    state.result.fetchTokenSuccess = true
  },

  fetchTokenFailure (state, err) {
    state.error = err
    state.loading = false
    state.result.fetchTokenSuccess = false
    console.error(err)
  },

  login (state) {
    state.error = null
    state.loading = true
    state.result.loginSuccess = null
  },

  loginSuccess (state) {
    state.loading = false
    state.result.loginSuccess = true
  },

  loginFailure (state, err) {
    state.error = err
    state.result.loginSuccess = (state.error.status === 400) ? false : null
  },

  passwordRecovery (state) {
    state.error = null
    state.loading = true
    state.result.passwordRecoveryRequestSuccess = null
  },

  updateMeta (meta) {
    state.meta = meta
  },

  passwordRecoverySuccess (state, data) {
    state.loading = false
    state.result.passwordRecoveryRequestSuccess = true
  },

  passwordRecoveryFailure (state, err) {
    state.error = err
    state.loading = false
    state.result.passwordRecoveryRequestSuccess = false
  },

  save (state) {
    state.error = null
    state.loading = true
  },

  saveSuccess (state, data) {
    if (data.email) {
      Security.user = data
      state.data = setFullName(data)
    }

    state.modals.alert = {
      show: true,
      options: {
        type: 'success',
        bodyKey: 'profile.modals.saveSuccess.text'
      }
    }

    state.loading = false
  },

  saveFailure (state, err) {
    state.error = err
    state.loading = false
    state.modals.alert = {
      show: true,
      options: {
        type: 'error',
        bodyKey: err.message ? err.message : 'profile.modals.saveFailure.text'
      }
    }

    console.error(err)
  },

  updateRoute (state, data) {
    state.route = data
  },

  userInfoRequest (state) {
    state.error = null
    state.loading = true
  },

  userInfoRequestSuccess (state, { data, meta }) {
    state.data = setFullName(data)
    state.meta = meta
    Security.user = data
    state.loading = false

    if (data && data.language && data.language.code) {
      axios.defaults.headers.common['Accept-Language'] = data.language.code
    }
  },

  userInfoRequestFailure (state, err) {
    state.error = err
    state.loading = false
    console.error(err)
  },

  saveUserLogoRequest (state) {
    state.error = null
    state.loading = true
  },

  saveUserLogoSuccess (state, data) {
    state.data = setFullName(data)
    state.loading = false
  },

  saveUserLogoFailure (state, err) {
    state.error = err
    state.loading = false
    console.error(err)
  },

  clearUser (state) {
    state.data = null
  },

  saveNewEmail (state, data) {
    state.data = setFullName(data)
  },

  updateUserPasswordInit (state) {
    state.error = null
    state.loading = true
    state.result.passwordUpdateSuccess = null
  },

  updateUserPasswordSuccess (state, data) {
    state.loading = false
    state.result.passwordUpdateSuccess = true
  },

  updateUserPasswordFailure (state, err) {
    state.error = err
    state.loading = false
    state.result.passwordUpdateSuccess = false
  },

  clearModalAlertData (state) {
    state.modals.alert = getInitialModalAlertParams()
  },

  set_change_user(state, val) {
    state.change_user = val;
  }
}

const getters = {
  error (state, getters) {
    return state.error
  },

  loading (state, getters) {
    return state.loading
  },

  route (state, getters) {
    return state.route
  },

  current (state, getters) {
    return state.data
  },

  company(state, getters) {
    return getters.current ? getters.current.company : null
  },

  accessToken (state, getters) {
    return Security.accessToken
  },

  // this stuff must be removed somehow when backend will deployed its realization with vacancy, response meta
  activeProcess (state) {
    if (!state.data || !state.meta) {
      return null
    }

    return state.meta.active_process || null
  },

  hasRole (state) {
    return (needed) => {
      return state.data && state.data.roles.some(({ role }) => role === needed)
    }
  },

  isAdmin (state, getters) {
    return ADMIN_ROLES.some(role => getters.hasRole(role))
  },

  isCreatedByAdmin (state, getters) {
    return state.data && !!state.data.is_created_by_admin;
  },

  isCompanyAdmin (state, getters) {
    return getters.hasRole(ROLE_COMPANY_ADMIN)
  },

  isCompanyUser (state, getters) {
    return getters.hasRole(ROLE_COMPANY_USER)
  },

  change_user(state, getters) {
    return state.change_user;
  },

  modal_alert(state, getters) {
    return state.modals.alert;
  }
}

export default {
  namespaced: true,
  state,
  actions,
  getters,
  mutations,
  modules: {
    notifications
  }
}
